import { useState } from "react";
import { Code2, Terminal, AlertTriangle } from "lucide-react";
import { MessageResponse } from "@/components/ai-elements/message";
import { CopyWrapper } from "./copy-wrapper";
import { StackTraceBlock } from "./stack-trace-block";
import type { ToolState } from "../types";

interface SandboxBlockProps {
  code: string;
  language: string;
  filename?: string;
  output?: string;
  error?: string;
  state?: ToolState;
  renderStackTrace?: boolean;
}

const STATE_LABELS: Partial<Record<string, { label: string; color: string }>> = {
  "input-streaming": { label: "Running", color: "text-yellow-500" },
  "input-available": { label: "Running", color: "text-yellow-500" },
  "output-available": { label: "Complete", color: "text-green-500" },
  "output-error": { label: "Error", color: "text-red-500" },
};

export function SandboxBlock({
  code,
  language,
  filename,
  output,
  error,
  state,
  renderStackTrace,
}: SandboxBlockProps) {
  const [activeTab, setActiveTab] = useState<"code" | "output">("code");
  const stateInfo = STATE_LABELS[state ?? "output-available"];
  const hasOutput = !!output || !!error;

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-border">
      {/* Header */}
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2">
        <Code2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {filename ?? `code.${language}`}
        </span>
        {stateInfo && (
          <span className={`ml-auto text-xs font-medium ${stateInfo.color}`}>
            {stateInfo.label}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-muted/30">
        <button
          type="button"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
            activeTab === "code"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("code")}
        >
          <Code2 className="h-3 w-3" />
          Code
        </button>
        <button
          type="button"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
            activeTab === "output"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("output")}
          disabled={!hasOutput}
        >
          <Terminal className="h-3 w-3" />
          Output
          {error && <AlertTriangle className="h-3 w-3 text-red-500" />}
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[400px] overflow-auto">
        {activeTab === "code" && (
          <CopyWrapper copyText={code} label="Copy code">
            <div className="p-3">
              <MessageResponse>
                {"```" + language + "\n" + code + "\n```"}
              </MessageResponse>
            </div>
          </CopyWrapper>
        )}
        {activeTab === "output" && (
          <CopyWrapper copyText={error || output || ""} label="Copy output">
            <div className="p-3">
              {error ? (
                renderStackTrace ? (
                  <StackTraceBlock error={error} />
                ) : (
                  <div className="rounded bg-red-500/10 p-2 text-sm text-red-600 dark:text-red-400">
                    <pre className="whitespace-pre-wrap font-mono text-xs">{error}</pre>
                  </div>
                )
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-xs">{output}</pre>
              )}
            </div>
          </CopyWrapper>
        )}
      </div>
    </div>
  );
}
